import {
  GeoCoordinates,
  ToolkitProperty,
  schSavedPairCriteria,
  schStudiesSearch,
} from '@aloft/toolkit-shared';
import { Block, Flex } from '@aloft/toolkit-sui';
import { useDisclosure } from '@aloft/toolkit-sui/chakra';

import { debounce } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useFormState } from 'react-hook-form';
import { unstable_usePrompt } from 'react-router-dom';

import { LoadingScreenSamFull } from '../../components/LoadingScreen/LoadingScreenSamFull.tsx';
import { Coordinates } from '../../components/map/types.ts';
import { useLocalStorage } from '../../controller/hooks/useLocalStorage.ts';
import { useConfirmTabClose } from '../../hooks/useConfirmTabClose.ts';
import { useToaster } from '../../hooks/useToaster/useToaster.tsx';
import { Reconcile } from './Reconcile.tsx';
import { AdjustPairedSalesLayout } from './components/AdjustPairedSalesLayout.tsx';
import { AdjustPairs } from './components/AdjustPairs/AdjustPairs.tsx';
import { FilterDrawer } from './components/FilterDrawer/FilterDrawer.tsx';
import { PickPair } from './components/PickPair/PickPair.js';
import { usePairedSalesInternalState } from './hooks/useAdjustPairedInternalState.js';
import { useGetPropertiesAndPairs } from './hooks/useGetPropertiesAndPairs.ts';
import { AdjustPairedSalesStandaloneModuleProps, PairedSaleIDs } from './types.js';

export const AdjustPairedSalesStandaloneModule = ({
  initialStudy,
  isEditing,
  mapProps,
  getDisplayValue,
  onCancel,
  onSave,
  onFinish,
}: AdjustPairedSalesStandaloneModuleProps) => {
  const {
    form,
    onSubmit,
    onInvalid,
    activePair,
    setActivePair,
    selectPair,
    deselectPair,
    setGeoSearch,
    getGeoSearch,
    handleSwapProperties,
  } = usePairedSalesInternalState(initialStudy, onSave);

  const [properties, setProperties] = useState<ToolkitProperty[]>([]);
  const recordIDToPropertyMap = useMemo(() => {
    return Object.fromEntries(properties.map((x) => [x.toolkitRecordID, x]));
  }, [properties]);

  const makeToast = useToaster();

  useEffect(() => {
    window.history.replaceState({}, document.title);
  }, []);

  useConfirmTabClose({ when: true });

  const { isSubmitSuccessful } = useFormState({ control: form.control });

  useEffect(() => {
    if (isSubmitSuccessful) {
      setTimeout(() => {
        onFinish();
      }, 500);
    }
  }, [isSubmitSuccessful]);

  unstable_usePrompt({
    message: 'Your unsaved changes will be lost. Are you sure you want to leave?',
    when: ({ currentLocation, nextLocation }) => {
      return !isSubmitSuccessful && currentLocation.pathname !== nextLocation.pathname;
    },
  });

  const {
    isOpen: isFilterDrawerOpen,
    onOpen: onFilterDrawerOpen,
    onClose: onFilterDrawerClose,
  } = useDisclosure();

  const [step, setStep] = useState(isEditing ? 1 : 0);

  const term = initialStudy?.input.analysisFeature;

  const [shouldShowLoader, setShouldShowLoader] = useState(false);
  const [pairs, setPairs] = useState<PairedSaleIDs[]>([]);

  const [stashedMapState, setStashedMapState] = useLocalStorage<
    Pick<AdjustPairedSalesStandaloneModuleProps['mapProps'], 'center' | 'zoom'>
  >({
    key: 'paired-sales-map',
    defaultValue: {
      ...mapProps,
    },
  });
  const debouncedSetStashedMapState = debounce(setStashedMapState, 1000);

  const { coords: coordinates } = getGeoSearch();

  const { getPropertiesAndPairs } = useGetPropertiesAndPairs({ getDisplayValue });

  // end hooks!
  const refreshMapData = async ({ coordinates }: { coordinates: GeoCoordinates }) => {
    setShouldShowLoader(true);

    try {
      const { pairs: newPairs, properties: newProperties } = await getPropertiesAndPairs({
        coordinates,
        search: schStudiesSearch.parse(form.getValues('search')),
        pairCriteria: schSavedPairCriteria.parse(form.getValues('search.pairCriteria')),
      });
      setPairs(newPairs);
      setProperties(newProperties);
    } catch (error) {
      console.error('Error fetching properties: ', error);
      makeToast({
        id: 'fetch-listings-error',
        type: 'error',
        title: 'Error fetching properties',
        body: 'There was an issue fetching properties. Try modifying your boundary or deleting it and drawing a smaller boundary.',
      });
    }

    setShouldShowLoader(false);
  };

  const applyFilters = () => {
    onFilterDrawerClose();
    refreshMapData({ coordinates });
  };

  const isMounted = useRef(false);
  useEffect(() => {
    if (isEditing && !isMounted.current) {
      refreshMapData({ coordinates });
    }
    isMounted.current = true;
  });

  let stepContent: React.ReactNode;

  if (step === 0) {
    stepContent = (
      <>
        <LoadingScreenSamFull
          isLoading={shouldShowLoader}
          what="properties"
          lightText
          wrapperProps={{
            __dangerDoNotUseSx: {
              background: 'rgba(0, 0, 0, 0.6)',
            },
          }}
        />
        <PickPair
          term={term}
          pairs={pairs}
          recordIDToPropertyMap={recordIDToPropertyMap}
          properties={properties}
          mapProps={{
            subject: mapProps.subject,
            center: stashedMapState.center,
            zoom: stashedMapState.zoom,
          }}
          onMoveEnd={(center: Coordinates, zoom: number) =>
            debouncedSetStashedMapState({ center, zoom })
          }
          onSelectPair={(p1, p2) => selectPair(p1, p2)}
          onDeselectPair={(p1, p2) => deselectPair(p1, p2)}
          onPolygonCreateAndUpdate={async (coordinates) => {
            setGeoSearch({
              t: 'polygon',
              coords: coordinates,
            });
            await refreshMapData({ coordinates });
          }}
          onPolygonDrawDelete={async () => {
            setPairs([]);
            setProperties([]);
            setGeoSearch({
              t: 'polygon',
              coords: [],
            });
          }}
          coordinates={coordinates}
          onClickEditFilters={onFilterDrawerOpen}
          isLoading={shouldShowLoader}
        />
      </>
    );
  } else if (step === 1 && activePair !== null) {
    stepContent = (
      <Flex height="100%" width="100%" overflow="hidden">
        <AdjustPairs
          key={activePair}
          pairIndex={activePair ?? 0}
          term={term}
          onSwapProperties={handleSwapProperties}
        />
        <Block width="710px" height="100%">
          <Reconcile
            term={term}
            activePairIndex={activePair ?? 0}
            onRowSelect={(pairIndex: number) => setActivePair(pairIndex)}
            onDeletePair={(p1, p2) => deselectPair(p1, p2)}
          />
        </Block>
      </Flex>
    );
  } else {
    throw new Error(`Invalid step: ${step}`);
  }

  return (
    <FormProvider {...form}>
      <AdjustPairedSalesLayout
        step={step}
        onCancelButtonClick={onCancel}
        onNextButtonClick={() => setStep((s) => s + 1)}
        onBackButtonClick={() => setStep((s) => s - 1)}
        onSubmitButtonClick={() => {
          form.handleSubmit(onSubmit, onInvalid)();
        }}
      >
        <Block flex={1} overflow="auto" position="relative">
          {stepContent}
        </Block>
        <FilterDrawer
          isOpen={isFilterDrawerOpen}
          onCancel={onFilterDrawerClose}
          onApply={applyFilters}
        />
      </AdjustPairedSalesLayout>
    </FormProvider>
  );
};
